import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { DisplayListService } from "../../services/display-list.service";
import { DisplayListListItemModel, FilterModel, ListItemsFilterModel, MediaAppListItemModel } from "../../models";
import { BaseService } from "../../services";
import { AppUtils } from "src/helpers";
import { PlanAccessService } from 'src/services';
import { addDarkToImageName, getDynamicMediaAppIconUrl } from 'src/app/shared/services/functions';
import { NzDrawerComponent } from "ng-zorro-antd/drawer";
import { NzModalService } from "ng-zorro-antd/modal";
import { Subscription } from "rxjs";
import { SharedModule } from '../../../shared/shared.module';
import { DisplayListUpsertComponent } from "./upsert/display-list-upsert.component";
import { DisplayUpsertComponent } from "../display/upsert/display-upsert.component";
import { DisplayLivePreviewComponent } from 'src/app/display/components/live-preview/display-live-preview.component';

@Component({
    standalone: true,
    imports: [SharedModule, DisplayListUpsertComponent],
    selector: 'app-display-list-list',
    templateUrl: './display-list-list.component.html',
    styleUrls: ['./display-list-list.component.scss']
})

export class PlaylistComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('drawer') drawer: NzDrawerComponent;
    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;
    @Input() groupId: number;
    @Input() addFromDisplay: boolean;
    @Input() medias: Array<MediaAppListItemModel> = new Array<MediaAppListItemModel>();
    @Input() multiSelect: boolean = false;
    @Input() preSelectedItems: Array<DisplayListListItemModel> = [];
    @Output() closeDrawer: EventEmitter<{ selectedDisplayList: DisplayListListItemModel, selectedDisplayLists?: Array<DisplayListListItemModel>, updateValue: boolean }> = new EventEmitter<{ selectedDisplayList: DisplayListListItemModel, selectedDisplayLists?: Array<DisplayListListItemModel>, updateValue: boolean }>();
    @Output() selectionChange = new EventEmitter<Array<DisplayListListItemModel>>();
    isModelLoaded: boolean;
    filterKey: string;
    model: Array<DisplayListListItemModel>;
    isLoading = true;
    sortOrder: string;
    hasDisplayList: boolean = true;
    addDisplayList: boolean;
    displayListId: number;
    totalRecords: number = 0;
    recordsToSkip: number;
    pageSize: number;
    loadingMore = false;
    hasMore = true;
    page = 1;
    filterModel = new FilterModel();
    subscriptions = new Array<Subscription>();
    selectedItems: Array<DisplayListListItemModel> = [];
    showMaxLimitWarning = false;
    isPlaylistSelected = false;
    readonly MAX_PLAYLISTS = 10;

    constructor(private service: DisplayListService,
        private baseService: BaseService,
        private appUtils: AppUtils,
        private modal: NzModalService,
        private planAccess: PlanAccessService
    ) {
        this.filterModel.sortBy = 'name';
        this.filterModel.sortDir = 'asc';
    }

    ngOnInit(): void {
        if (this.multiSelect && this.preSelectedItems?.length > 0) {
            this.selectedItems = [...this.preSelectedItems];
            this.isPlaylistSelected = this.selectedItems.length > 0;
        }
        this.getList(true);
    }

    ngAfterViewInit() {
        this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
    }

    trackById(index: number, item: any): number {
        return item.id;
    }

    onScroll(event: any) {
        const element = this.scrollContainer.nativeElement;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        if (scrollTop + clientHeight >= scrollHeight * 0.8 && this.hasMore && !this.loadingMore) {
            this.getMoreRecords();
        }
    }

    getList(initialLoading = false, limit = 15) {
        this.isModelLoaded = false;
        this.isLoading = true;
        this.filterModel.limit = limit;
        this.filterModel.offset = 0;
        this.totalRecords = 0;
        const requestSubscription = 
        this.service.getList(this.groupId, this.filterModel)
            .subscribe({
                next: (response: Array<DisplayListListItemModel>) => {
                    this.model = response;
                    this.totalRecords = response.length;
                    if (this.model.length > 0) {
                        this.model.forEach((x) => {
                            x.defaultUserMediaAppUrl = this.getMediaIconUrl(x.defaultUserMediaAppType, x.appProperties);
                            x.defaultUserMediaAppUrlDark = this.getMediaIconUrlDark(x.defaultUserMediaAppType, x.appProperties);
                            x.showDefaultIcon = true;
                            if (this.multiSelect) {
                                x.isSelected = this.selectedItems.some(s => s.id === x.id);
                            }
                        });
                    }

                    if (initialLoading) {
                        this.hasDisplayList = this.model.length > 0;
                    }
                    this.isModelLoaded = true;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.baseService.processErrorResponse(error);
                    this.isLoading = false;
                }
            });
            this.subscriptions.push(requestSubscription);
    }

    getMediaIconUrl(mediaType: string, appProperties?: string): string {
        if (mediaType === 'DynamicMediaApp' && appProperties) {
            const dynamicIcon = getDynamicMediaAppIconUrl(appProperties);
            if (dynamicIcon) { return dynamicIcon; }
        }
        const media = this.medias.find(x => x.id == mediaType);
        if (media != undefined) {
            return media.iconUrl;
        }
        if (mediaType) {
            return `./assets/images/media-types/icons/v2/${mediaType}.svg`;
        }
        return './assets/images/media-types/icons/v2/DynamicMediaApp.svg';
    }

    getMediaIconUrlDark(mediaType: string, appProperties?: string): string {
        if (mediaType === 'DynamicMediaApp' && appProperties) {
            const dynamicIcon = getDynamicMediaAppIconUrl(appProperties);
            if (dynamicIcon) { return addDarkToImageName(dynamicIcon); }
        }
        const media = this.medias.find(x => x.id == mediaType);
        if (media?.iconUrlDark) {
            return media.iconUrlDark;
        }
        return addDarkToImageName(this.getMediaIconUrl(mediaType, appProperties));
    }

    openDisplaylistUpsertDrawer() {
        // Plan enforcement: creating a new playlist from this nested selection drawer must
        // respect the max_playlists limit, same as the route/manage entry points (DN-3893).
        if (!this.planAccess.guardLimit('max_playlists')) return;
        this.addDisplayList = true;
        this.drawer.open();
    }

    close() {

    }

    closeDisplayUpsertDrawer(event: any): void {
        if (event.displayListAdded) {
            this.displayListId = event.displayListId;
            this.getList(true);
        }
        this.drawer.close();
        this.addDisplayList = false;
    }

    onSearch() {
        if (this.filterModel.filterKey) {
            this.getList();
        }
    }

    clearSearch() {
        this.filterModel.filterKey = '';
        this.getList();
    }

    selectPlaylist(item: DisplayListListItemModel): void {
        if (!this.multiSelect) {
            this.closeDrawer.emit({ selectedDisplayList: item, updateValue: true });
            return;
        }

        const willBeSelected = !item.isSelected;

        if (willBeSelected && this.selectedItems.length >= this.MAX_PLAYLISTS) {
            this.showMaxLimitWarning = true;
            return;
        }

        item.isSelected = willBeSelected;
        this.syncSelectedItems(item);
        this.showMaxLimitWarning = this.selectedItems.length >= this.MAX_PLAYLISTS;
    }

    // Called by checkbox (ngModelChange) — item.isSelected already set by [(ngModel)]
    onCheckboxToggle(item: DisplayListListItemModel, checked: boolean): void {
        if (checked && this.selectedItems.length > this.MAX_PLAYLISTS) {
            item.isSelected = false;
            this.showMaxLimitWarning = true;
            return;
        }
        this.syncSelectedItems(item);
        this.showMaxLimitWarning = this.selectedItems.length >= this.MAX_PLAYLISTS;
    }

    private syncSelectedItems(item: DisplayListListItemModel): void {
        if (item.isSelected) {
            if (!this.selectedItems.some(s => s.id === item.id)) {
                this.selectedItems.push(item);
            }
        } else {
            this.selectedItems = this.selectedItems.filter(s => s.id !== item.id);
        }
        this.isPlaylistSelected = this.selectedItems.length > 0;
        this.selectionChange.emit([...this.selectedItems]);
    }

    done(): void {
        this.closeDrawer.emit({ selectedDisplayList: null, selectedDisplayLists: this.selectedItems, updateValue: true });
    }

    cancel(): void {
        this.closeDrawer.emit({ selectedDisplayList: new DisplayListListItemModel(), updateValue: false });
    }

    sortPlaylist() {
        if (this.sortOrder === 'ascending') {
            this.filterModel.sortBy = 'name';
            this.filterModel.sortDir = 'asc';
        } else if (this.sortOrder === 'descending') {
            this.filterModel.sortBy = 'name';
            this.filterModel.sortDir = 'desc';
        }
        this.getList(false, this.totalRecords);
    }

    filterKeyChanged() {
        if (this.appUtils.isNullOrEmpty(this.filterModel.filterKey)) {
            this.getList();
        }
    }

    onImageError(event: Event): void {
        const img = event.target as HTMLImageElement;
        img.src = './assets/images/media-types/icons/v2/DynamicMediaApp.svg';
        img.onerror = null;
    }

    previewPlaylistInModal(item: DisplayListListItemModel): void {
        if (!item?.id) {
            this.baseService.error('Preview URL is not available for this playlist.');
            return;
        }
        if (!item.userMediaAppCount || item.userMediaAppCount === 0) {
            this.baseService.error('Please add media to preview the playlist.');
            return;
        }
        this.modal.create({
            nzContent: DisplayLivePreviewComponent,
            nzData: { displayListId: item.id },
            nzWidth: '80vw',
            nzFooter: null,
            nzZIndex: 1100
        });
    }

    closeMediaStoreDrawer(event: any) {

    }

    getMoreRecords() {
        if (this.loadingMore) { return; }
        this.filterModel.limit = 5;
        this.filterModel.offset = this.totalRecords;
        this.loadingMore = true;
        this.isLoading = true;
        this.service.getList(this.groupId, this.filterModel)
            .subscribe({
                next: (response: Array<DisplayListListItemModel>) => {
                    if (response && response.length > 0) {
                        response.forEach((x) => {
                            x.defaultUserMediaAppUrl = this.getMediaIconUrl(x.defaultUserMediaAppType, x.appProperties);
                            x.defaultUserMediaAppUrlDark = this.getMediaIconUrlDark(x.defaultUserMediaAppType, x.appProperties);
                            x.showDefaultIcon = true;
                            if (this.multiSelect) {
                                x.isSelected = this.selectedItems.some(s => s.id === x.id);
                            }
                            this.model.push(x);
                        });
                        this.totalRecords += response.length;
                    }
                    else {
                        this.hasMore = false;
                    }
                    this.loadingMore = false;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.loadingMore = false;
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    ngOnDestroy() {
        this.isLoading = false;
        this.subscriptions.forEach(s => { s.unsubscribe(); });
    }

}
